import IRepository from "data/IRepository";
import { inversifyContainer } from "infrastructure/ioc/ioc.container";
import { IocTypes } from "infrastructure/ioc/ioc.types";
import { injectable } from "inversify";
import RequirementDTO from "../dtos/RequirementDTO";
import IRequirementCreateService from "./IRequirementCreateService";
import { Requirements_InsertRequirement } from 'data/endpoints';
import { store } from "infrastructure/store/ConfigureStore";
import { EnabledRequirementType } from "../enums/enabledRequirementType";
import DocumentSimplifiedDTO from "features/invoices/dtos/DocumentSimplifiedDTO";
import { trackPromise } from "react-promise-tracker";
import PostResponse from "data/PostResponse";
import InsertRequirementResultDTO from "features/requirements/dtos/InsertRequirementResultDTO";
import { FormFields } from "features/shared/forms/hooks/useForm";
import DataDescriptorDTO from "features/shared/dtos/DataDescriptorDTO";
import FormFieldDescriptor from "features/shared/forms/dynamicForm/models/FormFieldDescriptor";
import { TFunction } from "i18next";
import { getTranslatedText } from "features/shared/forms/dynamicForm/helpers/dynamicFormHelper";
import { getQueryParams } from "features/shared/helpers/queryParams";
import ValidationService from "features/validation/ValidationService";

@injectable()
export default class RequirementCreateService implements IRequirementCreateService {
  //#region Fields
  private readonly _repository: IRepository<RequirementDTO>;
  private readonly _validationService: ValidationService;
  //#endregion Fields

  //#region Ctor
  constructor() {
      this._repository = inversifyContainer.get<IRepository<object>>(IocTypes.Repository);
      this._validationService = new ValidationService();
  }
  //#endregion Ctor

  //#region Public
  async createRequirement(type: EnabledRequirementType, formValues: Record<string, any>): Promise<PostResponse<InsertRequirementResultDTO>> {
    const requirementDTO = this.getRequirementDTO(type, formValues);

    let result = this.postRequirement(requirementDTO, formValues["files"]);

    return result;
  }

  async createDynamicRequirement(
    requirementType: string,
    formValues: FormFields,
    formDesriptors: FormFieldDescriptor[],
    translateLabel: TFunction
  ): Promise<PostResponse<InsertRequirementResultDTO>> {
    const descriptors = this.getDynamicRequirementDescriptors(formValues, formDesriptors, translateLabel);
    const files: FormData = this.getFilesFromDescriptors(formValues, formDesriptors);
    const documents = this.getDocuments(files, "Other");

    let requirement: RequirementDTO = {
      customerId: this.getCustomerId(),
      requirementType: requirementType,
      description: formValues["Description"],
      requirementDescriptors: descriptors,
      documents: documents
    };
    
    let result = this.postRequirement(requirement, files, true);

    return result;
  }
  //#region Public

  //#region Private
  private getRequirementDTO(type: EnabledRequirementType, formValues: Record<string, any>) {
    const descriptors = this.getDescriptors(formValues, "DESC_");
    const documents = this.getDocuments(formValues["files"], formValues["documentsType"]);

    const requirement: RequirementDTO = {
      customerId: this.getCustomerId(),
      requirementType: type,
      description: formValues["description"],
      requirementDescriptors: descriptors,
      documents: documents
    };

    return requirement;
  }

  private getDescriptors(formValues: Record<string, any>, descPrefix: string) {
    let result: DataDescriptorDTO[] = [];
    for (const [key, value] of Object.entries(formValues)) {
      if (key.startsWith(descPrefix)) {
        const descriptor: DataDescriptorDTO = {
          code: key.replace(descPrefix, ""),
          value: value === null ? null : String(value)
        }
        result.push(descriptor);
      }
    }
    
    return result;
  }

  private getCustomerId() {
    const customerState = store.getState().customer;
    return customerState.customer.customerId;
  }

  private getDocuments (files: FileList | FormData | null, documentType: string) {
    if (files) {
      let documents: DocumentSimplifiedDTO[] = [];

      if (files instanceof FormData) {

        for (const [key, file] of files.entries()) {
          documents.push({name: (file as File).name, documentType: documentType});
      }
      } else if (files instanceof FileList) {
        for (const file of files) {
          documents.push({name: file.name, documentType: documentType});
        }
      }

      return documents;
    }
  }

  private getDynamicRequirementDescriptors(
    formValues: FormFields,
    formDesriptors: FormFieldDescriptor[],
    translateLabel: TFunction
  ): DataDescriptorDTO[] {
    let result: DataDescriptorDTO[] = [];
    let fileDescNames = this.getFileDescriporNames(formDesriptors);
    let keysToSkip = ["Description", ...fileDescNames]; // properties of main object (not include to descriptors)

    for (const formDesc of formDesriptors) {
      if (!keysToSkip.includes(formDesc.name)) {
        // value for form descriptor (filled data)
        let value = formValues[formDesc.name];
        if (this._validationService.validateReuired(value)) { // create descriptor from not empty value
          const stringValue = value === null ? null : String(value);
          const descriptor: DataDescriptorDTO = {
            code: formDesc.name,
            codeLabel: this.getDynamicDescriptorLabel(formDesc, translateLabel),
            value: stringValue,
            valueLabel: this.getDynamicDescriptorValueLabel(formDesc, stringValue, translateLabel),
          };

          result.push(descriptor);
        }
      }
    }

    return result;
  }

  private async postRequirement(requirement: RequirementDTO, files: FileList | FormData, isDynamic?: boolean): Promise<PostResponse<InsertRequirementResultDTO>> {
    let result = await trackPromise(
      this._repository.postWithFile<InsertRequirementResultDTO>({
        url: Requirements_InsertRequirement,
        multiple: true,
        files: files,
        filesKey: "Files",
        object: requirement,
        objectKey: "Requirement",
        params: isDynamic ? getQueryParams("IsDynamicRequirement", `${isDynamic}`) : undefined
      })
    );

    return result;
  }

  private getFileDescriporNames(
    formDesriptors: FormFieldDescriptor[]
  ): string[] {
    let descriptorNames: string[] = formDesriptors
      .filter((x) => x.type === "FileBox" || x.type === "FileBoxMultiple")
      .map((x) => x.name);

    return descriptorNames;
  }

  private getFilesFromDescriptors(formValues: FormFields, formDesriptors: FormFieldDescriptor[]): FormData {
    let formDataFiles = new FormData();
    let fileDescNames = this.getFileDescriporNames(formDesriptors);

    for (const descName of fileDescNames) {
      let files: FileList = formValues[descName];

      if (files?.length > 0) {
        for (const file of files) {
          formDataFiles.append(file.name, file);
        }
      }
    }

    return formDataFiles;
  }

  private getDynamicDescriptorLabel(formDesriptor: FormFieldDescriptor, translateLabel: TFunction): string {
    let label = getTranslatedText(formDesriptor, "label", translateLabel); // translate for descriptor

    if (formDesriptor.inputGroup?.includeToOutput) {
      label = `${label} (${formDesriptor.inputGroup?.content})`;
    }

    return label;
  }

  // valueLabel je přeložená hodnota pro zobrazení (email, detail). Do CRM jde dál Value (kód).
  // Řešeno obecně pro SelectBox napojené na enum s definovanou enumTranslationPath.
  private getDynamicDescriptorValueLabel(
    formDesriptor: FormFieldDescriptor,
    value: string | null,
    translate: TFunction
  ): string | undefined {
    const enumTranslationPath = formDesriptor.options?.enumTranslationPath;

    if (!value || !enumTranslationPath) {
      return undefined;
    }

    const valueLabel = translate(value, { ns: enumTranslationPath });

    // pokud překlad neexistuje, i18next vrátí klíč => zachováme dosavadní chování
    return valueLabel !== value ? valueLabel : undefined;
  }
  //#endregion Private
}